import { Building2, Filter } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FlatfinderData } from 'realitiq-utils/src/flatfinder';
import { Modal } from '@/components/modals/new/modal';
import { Button } from '@/components/ui/button';
import {
  getFlatfinderImageUrl,
  getSafeFlatfinderContactUrl,
  getSafeFlatfinderExternalUrl,
} from './display';
import { EstateDetailModal } from './viewer-estate-detail-modal';
import { ViewerEstateListItem } from './viewer-estate-list-item';
import {
  createInitialFlatfinderFilters,
  estateMatchesFlatfinderFilters,
  FlatfinderFilterFields,
  type FlatfinderFilters,
} from './viewer-filters';
import { ViewerPerspective } from './viewer-perspective';

export function FlatfinderViewer({
  data,
  embed = false,
  authenticatedPreview = false,
}: {
  data: FlatfinderData;
  embed?: boolean;
  authenticatedPreview?: boolean;
}) {
  const { t } = useTranslation();
  const estatesById = useMemo(
    () => new Map(data.estates.map((estate) => [estate.id, estate])),
    [data.estates]
  );
  const perspectives = useMemo(
    () =>
      data.perspectives
        .filter((item) =>
          item.regions.some(
            (region) => region.estateId !== null && estatesById.has(region.estateId)
          )
        )
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [data.perspectives, estatesById]
  );
  const buildingsById = useMemo(
    () => new Map(data.project.buildings.map((building) => [building.id, building.name])),
    [data.project.buildings]
  );
  const linkedEstateIds = useMemo(
    () =>
      new Set(
        perspectives.flatMap((item) =>
          item.regions.flatMap((region) => (region.estateId ? [region.estateId] : []))
        )
      ),
    [perspectives]
  );
  const viewerEstates = useMemo(
    () => data.estates.filter((estate) => linkedEstateIds.has(estate.id)),
    [data.estates, linkedEstateIds]
  );
  const currencies = useMemo(
    () => [...new Set(viewerEstates.map((estate) => estate.currency).filter(Boolean))].sort(),
    [viewerEstates]
  );
  const [perspectiveIndex, setPerspectiveIndex] = useState(0);
  const [filters, setFilters] = useState<FlatfinderFilters>(createInitialFlatfinderFilters);
  const [hoveredEstateId, setHoveredEstateId] = useState<string | null>(null);
  const [selectedEstateId, setSelectedEstateId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activePerspectiveIndex = Math.min(perspectiveIndex, Math.max(0, perspectives.length - 1));
  const perspective = perspectives[activePerspectiveIndex];
  const visibleEstates = useMemo(
    () =>
      viewerEstates
        .filter((estate) => estateMatchesFlatfinderFilters(estate, filters))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [viewerEstates, filters]
  );
  const visibleEstateIds = useMemo(
    () => new Set(visibleEstates.map((estate) => estate.id)),
    [visibleEstates]
  );
  const hoveredEstate =
    hoveredEstateId && visibleEstateIds.has(hoveredEstateId)
      ? estatesById.get(hoveredEstateId)
      : undefined;
  const hoveredRegion = hoveredEstateId
    ? perspective?.regions.find(
        (region) => region.estateId === hoveredEstateId && visibleEstateIds.has(hoveredEstateId)
      )
    : undefined;
  const selectedEstate =
    selectedEstateId && visibleEstateIds.has(selectedEstateId)
      ? estatesById.get(selectedEstateId)
      : undefined;

  useEffect(() => {
    // Clear hidden selections so resetting filters cannot reopen an old detail modal.
    setSelectedEstateId((id) => (id && visibleEstateIds.has(id) ? id : null));
    setHoveredEstateId((id) => (id && visibleEstateIds.has(id) ? id : null));
  }, [visibleEstateIds]);
  const primaryColor = data.branding?.primaryColor || '#0f172a';
  const mediaPublicId = authenticatedPreview ? null : data.publicId;
  const authenticatedProjectId = authenticatedPreview ? data.projectId : null;
  const safeContactLink = getSafeFlatfinderContactUrl(data.contactLink);
  const legalNoticeLink = getSafeFlatfinderExternalUrl(data.project.legalNoticeLink);
  const privacyPolicyLink = getSafeFlatfinderExternalUrl(data.project.privacyPolicyLink);

  function showPerspectiveForEstate(estateId: string) {
    if (perspective?.regions.some((region) => region.estateId === estateId)) return;
    const nextPerspectiveIndex = perspectives.findIndex((item) =>
      item.regions.some((region) => region.estateId === estateId)
    );
    if (nextPerspectiveIndex >= 0) setPerspectiveIndex(nextPerspectiveIndex);
  }

  function showPreviousPerspective() {
    setHoveredEstateId(null);
    setPerspectiveIndex(
      (current) =>
        (Math.min(current, perspectives.length - 1) - 1 + perspectives.length) % perspectives.length
    );
  }

  function showNextPerspective() {
    setHoveredEstateId(null);
    setPerspectiveIndex(
      (current) => (Math.min(current, perspectives.length - 1) + 1) % perspectives.length
    );
  }

  function hoverEstate(estateId: string, hovered: boolean) {
    if (hovered) showPerspectiveForEstate(estateId);
    setHoveredEstateId(hovered ? estateId : null);
  }

  function selectEstate(estateId: string) {
    showPerspectiveForEstate(estateId);
    setSelectedEstateId(estateId);
  }

  if (!perspective) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center bg-slate-50 p-8 text-center text-slate-600">
        {t('noMatchingUnits')}
      </div>
    );
  }

  return (
    <div
      className={`${embed || !authenticatedPreview ? 'min-h-screen' : 'min-h-[70vh]'} w-full max-w-full overflow-x-hidden bg-slate-100 text-slate-950`}
    >
      <header className="border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
        <div className="mx-auto flex w-full min-w-0 max-w-[1600px] items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
            {data.branding?.logoMediaId ? (
              <img
                alt={data.branding.name}
                className="h-9 w-auto max-w-32 shrink-0 object-contain"
                crossOrigin={authenticatedPreview ? 'use-credentials' : undefined}
                src={getFlatfinderImageUrl(
                  data.branding.logoMediaId,
                  mediaPublicId,
                  authenticatedProjectId
                )}
              />
            ) : (
              <div
                className="flex size-9 shrink-0 items-center justify-center rounded-xl text-white"
                style={{ backgroundColor: primaryColor }}
              >
                <Building2 className="size-5" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="break-words text-base font-semibold leading-tight sm:text-lg">
                {data.project.name}
              </p>
              {data.branding?.name ? (
                <p className="break-words text-xs text-slate-500">{data.branding.name}</p>
              ) : null}
            </div>
          </div>
          {safeContactLink ? (
            <Button asChild variant="contained" className="hidden shrink-0 sm:inline-flex">
              <a href={safeContactLink} rel="noopener noreferrer" target="_blank">
                {t('contact')}
              </a>
            </Button>
          ) : null}
        </div>
      </header>

      <main className="mx-auto grid w-full min-w-0 max-w-[1600px] gap-4 p-3 sm:p-5 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-5">
        <ViewerPerspective
          authenticatedPreview={authenticatedPreview}
          authenticatedProjectId={authenticatedProjectId}
          estatesById={estatesById}
          hoveredEstate={hoveredEstate}
          hoveredEstateId={hoveredEstateId}
          hoveredRegion={hoveredRegion}
          perspectiveIndex={activePerspectiveIndex}
          perspectives={perspectives}
          publicId={mediaPublicId}
          visibleEstateIds={visibleEstateIds}
          onHoverEstate={setHoveredEstateId}
          onNextPerspective={showNextPerspective}
          onPreviousPerspective={showPreviousPerspective}
          onSelectEstate={setSelectedEstateId}
        />

        <aside className="flex max-h-[70dvh] min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:max-h-[calc(100dvh-120px)]">
          <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-4 py-4">
            <h2 className="font-semibold">
              {t('units')} ({visibleEstates.length})
            </h2>
            <Button
              variant="outline"
              className="gap-2 rounded-full px-4 lg:px-4"
              type="button"
              onClick={() => setFiltersOpen(true)}
            >
              <Filter className="size-4" />
              {t('filter')}
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {visibleEstates.length > 0 ? (
              visibleEstates.map((estate) => (
                <ViewerEstateListItem
                  buildingName={
                    estate.buildingId ? buildingsById.get(estate.buildingId) : undefined
                  }
                  estate={estate}
                  hovered={hoveredEstateId === estate.id}
                  key={estate.id}
                  onHover={(hovered) => hoverEstate(estate.id, hovered)}
                  onSelect={() => selectEstate(estate.id)}
                />
              ))
            ) : (
              <p className="px-3 py-10 text-center text-sm text-slate-500">
                {t('noMatchingUnits')}
              </p>
            )}
          </div>
        </aside>
      </main>

      {filtersOpen ? (
        <Modal
          isOpen
          open={() => setFiltersOpen(true)}
          dismiss={() => setFiltersOpen(false)}
          title={t('filter')}
          className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto rounded-lg border border-primary-light/20 shadow-lg"
        >
          <Modal.Body>
            <FlatfinderFilterFields
              currencies={currencies}
              data={data}
              filters={filters}
              onChange={setFilters}
            />
            <div className="mt-6 flex justify-end gap-2 border-t pt-4">
              <Button
                variant="outline"
                type="button"
                onClick={() => setFilters(createInitialFlatfinderFilters())}
              >
                {t('reset')}
              </Button>
              <Button type="button" onClick={() => setFiltersOpen(false)}>
                {t('close')}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      ) : null}

      {legalNoticeLink || privacyPolicyLink ? (
        <footer className="mx-auto flex w-full max-w-[1600px] justify-end gap-4 px-5 pb-5 text-xs text-slate-500">
          {legalNoticeLink ? (
            <a href={legalNoticeLink} rel="noopener noreferrer" target="_blank">
              {t('legalNotice')}
            </a>
          ) : null}
          {privacyPolicyLink ? (
            <a href={privacyPolicyLink} rel="noopener noreferrer" target="_blank">
              {t('privacyPolicy')}
            </a>
          ) : null}
        </footer>
      ) : null}

      {selectedEstate ? (
        <EstateDetailModal
          authenticatedProjectId={authenticatedProjectId}
          buildingName={
            selectedEstate.buildingId ? buildingsById.get(selectedEstate.buildingId) : undefined
          }
          contactLink={data.contactLink}
          estate={selectedEstate}
          publicId={mediaPublicId}
          onClose={() => setSelectedEstateId(null)}
        />
      ) : null}
    </div>
  );
}
